import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Tenant } from "@/types";
import { checkSession, fetchExperienceContract, logout as apiLogout, setActiveOrganization, type SessionInfo } from "@/lib/api";
import { EXPERIENCE_CONTRACT_UNKNOWN, type ExperienceContract } from "@/lib/experience-contract";
import { primaryRole } from "@/lib/permissions";
import { resolveUserIdentity } from "@/lib/user-display";
import type { SessionEndedEvent } from "@/lib/session-expiry";

/** ALL-5747: which 2FA page the `mfa` step shows. */
export type MfaMode = "enrol" | "challenge";

type AuthMethod = "passkey" | "totp" | "hardware" | "email";

// "error" covers a genuine session/effective-access transport failure
// (network error or 5xx) — distinct from "login" (a clean, confirmed
// unauthenticated response). The shell must never render nav/org context
// built from fixture data while masquerading as either of those two states.
type AuthStep =
  | "loading"
  | "login"
  | "mfa"
  | "tenant-select"
  | "passkey-enrollment"
  | "session-expired"
  | "session-ended"
  | "terms"
  | "app"
  | "error";

interface AuthState {
  step: AuthStep;
  method: AuthMethod | null;
  selectedTenant: Tenant | null;
  processing: boolean;
  user: { name: string; email: string; initials: string; role: string } | null;
  session: SessionInfo | null;
  experience: ExperienceContract;
  /** Set when `step === "error"`; a short machine-readable cause for the failed bootstrap. */
  errorReason: "session_unreachable" | "experience_unreachable" | null;
  selectOrganization: (organizationId: string) => Promise<void>;
  initSession: () => Promise<void>;
  /** ALL-5722: the person accepted the current terms; re-check the session and load the app. */
  termsAccepted: () => Promise<void>;
  /**
   * ALL-5747 (specs/alloy-mfa S-13): set with the `mfa` step. `enrol` when the
   * session has no factor yet, `challenge` when it must confirm one.
   */
  mfaMode: MfaMode | null;
  /** True when enrolment is offered at level `enrol` (ENROLMENT_SUGGESTED): "Not now" may continue. */
  mfaOptional: boolean;
  /** The ENROLMENT_SUGGESTED prompt was dismissed ("Not now") in this sign-in. */
  mfaPromptDismissed: boolean;
  /** ALL-5747: a 2FA step passed; re-check the session (the gate opens, or the next step shows). */
  mfaCompleted: () => Promise<void>;
  /** ALL-5747: the ENROLMENT_SUGGESTED prompt's "Set up" — shows the enrol page in place of the app. */
  startMfaEnrolment: () => void;
  /** ALL-5747: "Not now" was recorded (POST /v1/mfa/enrolments/skip); back to the app, no prompt. */
  mfaEnrolmentSkipped: () => void;
  goLogin: () => void;
  goTenantSelect: () => void;
  goEnrollment: () => void;
  goSessionExpired: () => void;
  /** ALL-5718: the browser session ended (confirmed by GET /auth/session). */
  sessionEnded: (event: SessionEndedEvent) => void;
  /** Where to return after sign-in, set with the session-ended step. */
  sessionEndedReturnTo: string;
  /** False inside the loop-guard window: the state waits for the button. */
  sessionEndedAutoRedirect: boolean;
  selectTenant: (tenant: Tenant) => void;
  setProcessing: (v: boolean) => void;
  signOut: () => Promise<void>;
  differentUser: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      step: "loading",
      method: null,
      selectedTenant: null,
      processing: false,
      user: null,
      session: null,
      experience: EXPERIENCE_CONTRACT_UNKNOWN,
      errorReason: null,
      sessionEndedReturnTo: "/",
      sessionEndedAutoRedirect: true,
      mfaMode: null,
      mfaOptional: false,
      mfaPromptDismissed: false,

      initSession: async () => {
        set({ step: "loading" });
        const session = await checkSession();
        if (session.transportError) {
          set({
            step: "error",
            errorReason: "session_unreachable",
            session: null,
            user: null,
            experience: EXPERIENCE_CONTRACT_UNKNOWN,
          });
          return;
        }
        if (!session.authenticated) {
          set({
            step: "login",
            session: null,
            user: null,
            experience: EXPERIENCE_CONTRACT_UNKNOWN,
            errorReason: null,
            mfaMode: null,
            mfaOptional: false,
            mfaPromptDismissed: false,
          });
          return;
        }
        const identity = resolveUserIdentity(session);
        const user = {
          name: identity.name,
          email: identity.email,
          initials: identity.initials,
          role: primaryRole(session.roleNames),
        };
        // ALL-5747 (specs/alloy-mfa S-3, S-13): while the session's second factor is
        // pending every session-verified API answers 403 AUTH_MFA_REQUIRED, so
        // nothing else is fetched; the 2FA page replaces every route. Checked
        // before the terms: the backend gates MFA first, then terms.
        if (session.mfaState === "ENROLMENT_REQUIRED" || session.mfaState === "CHALLENGE_REQUIRED") {
          set({
            step: "mfa",
            mfaMode: session.mfaState === "ENROLMENT_REQUIRED" ? "enrol" : "challenge",
            mfaOptional: false,
            session,
            user,
            experience: EXPERIENCE_CONTRACT_UNKNOWN,
            errorReason: null,
          });
          return;
        }
        // ALL-5722 (specs/terms-acceptance-gate S-10): until the current terms are
        // accepted every session-verified API answers 403, so effective access is
        // not fetched; the terms page replaces every route.
        if (session.termsAcceptanceRequired === true) {
          set({ step: "terms", session, user, experience: EXPERIENCE_CONTRACT_UNKNOWN, errorReason: null });
          return;
        }
        const { contract, transportError: experienceTransportError } = await fetchExperienceContract();
        if (experienceTransportError) {
          set({
            step: "error",
            errorReason: "experience_unreachable",
            session,
            experience: EXPERIENCE_CONTRACT_UNKNOWN,
          });
          return;
        }
        set({
          step: "app",
          session,
          experience: contract,
          errorReason: null,
          user,
          mfaMode: null,
          mfaOptional: false,
        });
      },

      termsAccepted: () => get().initSession(),

      mfaCompleted: () => get().initSession(),
      startMfaEnrolment: () => {
        const { step, session } = get();
        if (step !== "app" || session?.mfaState !== "ENROLMENT_SUGGESTED") return;
        set({ step: "mfa", mfaMode: "enrol", mfaOptional: true });
      },
      mfaEnrolmentSkipped: () => {
        const { step, mfaOptional } = get();
        // Only an optional enrolment shown over a loaded app returns to it.
        if (step === "mfa" && mfaOptional) {
          set({ step: "app", mfaMode: null, mfaOptional: false, mfaPromptDismissed: true });
          return;
        }
        set({ mfaPromptDismissed: true });
      },

      goLogin: () => set({ step: "login", processing: false }),
      goTenantSelect: () => set({ step: "tenant-select", processing: false }),
      goEnrollment: () => set({ step: "passkey-enrollment", processing: false }),
      goSessionExpired: () => set({ step: "session-expired" }),
      sessionEnded: ({ returnTo, autoRedirect }) =>
        set({
          step: "session-ended",
          session: null,
          user: null,
          processing: false,
          sessionEndedReturnTo: returnTo,
          sessionEndedAutoRedirect: autoRedirect,
        }),
      selectTenant: (tenant) => set({ selectedTenant: tenant, step: "app", processing: false }),
      selectOrganization: async (organizationId) => {
        const current = get().experience;
        if (current.source === "api") {
          const experience = await setActiveOrganization(organizationId);
          // A failed switch keeps the last-known-good organization rather
          // than falling back to fixture data (see lib/api.ts).
          if (experience) set({ experience });
          return;
        }
        const match = current.memberships.find((membership) => membership.id === organizationId);
        if (!match) return;
        set({
          experience: {
            ...current,
            activeOrganization: {
              ...current.activeOrganization,
              id: match.id,
              label: match.label,
              tenantId: `${match.id.replace(/-org$/, "")}-tenant`,
              trustDomainId: `${match.id.replace(/-org$/, "")}-sandbox`,
            },
            memberships: current.memberships.map((membership) => ({
              ...membership,
              selected: membership.id === match.id,
            })),
          },
        });
      },
      setProcessing: (v) => set({ processing: v }),

      signOut: async () => {
        await apiLogout();
        set({
          step: "login",
          selectedTenant: null,
          processing: false,
          method: null,
          session: null,
          user: null,
          experience: EXPERIENCE_CONTRACT_UNKNOWN,
          errorReason: null,
          mfaMode: null,
          mfaOptional: false,
          mfaPromptDismissed: false,
        });
      },

      differentUser: () => set({ step: "login", selectedTenant: null, processing: false, method: null }),
    }),
    {
      name: "alloy-auth",
      partialize: (s) => ({ selectedTenant: s.selectedTenant, method: s.method }),
    },
  ),
);
